import { Suspense, useState } from 'react';
import { useModifierKeys } from '@gonfalon/dom/useModifierKeys';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { useKeyCombo } from '@gonfalon/react/useKeyCombo';
import { Alert, DialogTrigger, IconButton, Popover, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import { trackEnvironmentEvent } from '../analytics';
import { type SelectedEnvironment } from '../types';
import { useEventLocation } from '../utils/getEventLocation';

import { EnvironmentFallback } from './EnvironmentFallback';
import { SelectEnvironmentDialog } from './SelectEnvironmentDialog';

export function AddEnvironmentDialogTrigger({
  projectKey,
  excludedIds,
  onSelect,
}: {
  projectKey: string;
  excludedIds: Set<string>;
  onSelect: (environment: SelectedEnvironment) => void;
}) {
  const keys = useModifierKeys();
  const [isOpen, setOpen] = useState(false);
  const location = useEventLocation();

  useKeyCombo('.', () => {
    setOpen(true);
  });

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(open) => {
        setOpen(open);
        if (open) {
          trackEnvironmentEvent('View Another Environment Clicked', { location });
        }
      }}
    >
      <TooltipTrigger>
        <IconButton aria-label="Add environment" variant="minimal" size="medium" icon="add" />
        <Tooltip placement="bottom">View another environment</Tooltip>
      </TooltipTrigger>
      <Popover placement="bottom end">
        <Suspense fallback={<EnvironmentFallback />}>
          <ErrorBoundary
            severity="high"
            fallback={
              <Alert variant="inline" status="error">
                There was a problem fetching the environments. Try again later.
              </Alert>
            }
          >
            <SelectEnvironmentDialog
              projectKey={projectKey}
              excludedIds={excludedIds}
              onSelect={(v) => {
                trackEnvironmentEvent('Environment Selected', { environmentKey: v.key, location });
                !keys.current.shiftKey && setOpen(false);
                onSelect(v);
              }}
            />
          </ErrorBoundary>
        </Suspense>
      </Popover>
    </DialogTrigger>
  );
}
